/**
 * Change Requests Management Page
 * 
 * Admin/HR page to view and manage employee change requests.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { changeRequestsAPI } from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronRight,
  User,
  History,
  AlertCircle,
  Check,
  X,
  PlayCircle,
} from 'lucide-react';

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'dd.MM.yyyy', { locale: de });
  } catch {
    return dateStr;
  }
};

// Wenn der String ein ISO-Datum (YYYY-MM-DD) ist, in deutsches Format
// (TT.MM.YYYY) umwandeln. Sonst unverändert lassen.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T.*)?$/;
const formatIfDate = (val) => {
  if (typeof val !== 'string' || !val) return val;
  if (!ISO_DATE_RE.test(val.trim())) return val;
  try {
    const onlyDate = val.length > 10 ? val.substring(0, 10) : val;
    return format(parseISO(onlyDate), 'dd.MM.yyyy', { locale: de });
  } catch {
    return val;
  }
};

const STATUS_CONFIG = {
  pending: { label: 'Eingereicht', color: 'bg-yellow-500', icon: Clock },
  in_progress: { label: 'In Bearbeitung', color: 'bg-blue-500', icon: Loader2 },
  approved: { label: 'Genehmigt', color: 'bg-green-500', icon: CheckCircle2 },
  rejected: { label: 'Abgelehnt', color: 'bg-red-500', icon: XCircle },
};

const CATEGORY_LABELS = {
  contact: 'Kontaktdaten',
  bank: 'Bankverbindung',
  emergency: 'Notfallkontakt',
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <Badge className={`${config.color} text-white`}>
      <Icon className={`h-3 w-3 mr-1 ${status === 'in_progress' ? 'animate-spin' : ''}`} />
      {config.label}
    </Badge>
  );
};

export default function ChangeRequests() {
  const { user, canManageEmployees } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Action dialog state
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(''); // approve, reject, in_progress
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // History dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyRequest, setHistoryRequest] = useState(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const statusFilter = activeTab === 'all' ? undefined : activeTab;
      const response = await changeRequestsAPI.getAll({ status: statusFilter });
      setRequests(response.data);
    } catch (error) {
      console.error('Error loading change requests:', error);
      toast.error('Fehler beim Laden der Änderungsanträge');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const openActionDialog = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setRejectionReason('');
    setActionDialogOpen(true);
  };

  const handleAction = async () => {
    if (actionType === 'rejected' && !rejectionReason.trim()) {
      toast.error('Bitte geben Sie einen Ablehnungsgrund an');
      return;
    }
    
    setProcessing(true);
    try {
      await changeRequestsAPI.updateStatus(
        selectedRequest.id,
        actionType,
        actionType === 'rejected' ? rejectionReason.trim() : null
      );
      
      const actionLabels = {
        approved: 'genehmigt',
        rejected: 'abgelehnt',
        in_progress: 'in Bearbeitung gesetzt',
      };
      
      toast.success(`Antrag wurde ${actionLabels[actionType]}`);
      setActionDialogOpen(false);
      loadRequests();
    } catch (error) {
      console.error('Error processing request:', error);
      toast.error('Fehler beim Verarbeiten des Antrags');
    } finally {
      setProcessing(false);
    }
  };

  const openHistoryDialog = (request) => {
    setHistoryRequest(request);
    setHistoryDialogOpen(true);
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const inProgressCount = requests.filter(r => r.status === 'in_progress').length;

  if (!canManageEmployees && user?.role !== 'admin') {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">Keine Berechtigung</h2>
              <p className="text-muted-foreground">
                Sie haben keine Berechtigung, Änderungsanträge zu verwalten.
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="change-requests-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Änderungsanträge</h1>
          <p className="text-muted-foreground">
            Verwalten Sie Änderungsanträge von Mitarbeitern für persönliche Daten
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending" className="relative">
              Ausstehend
              {pendingCount > 0 && (
                <span className="ml-2 w-5 h-5 bg-yellow-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="in_progress" className="relative">
              In Bearbeitung
              {inProgressCount > 0 && (
                <span className="ml-2 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                  {inProgressCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved">Genehmigt</TabsTrigger>
            <TabsTrigger value="rejected">Abgelehnt</TabsTrigger>
            <TabsTrigger value="all">Alle</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {activeTab === 'pending' && 'Ausstehende Anträge'}
                  {activeTab === 'in_progress' && 'Anträge in Bearbeitung'}
                  {activeTab === 'approved' && 'Genehmigte Anträge'}
                  {activeTab === 'rejected' && 'Abgelehnte Anträge'}
                  {activeTab === 'all' && 'Alle Anträge'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-20" />
                    <Skeleton className="h-20" />
                    <Skeleton className="h-20" />
                  </div>
                ) : requests.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Keine Änderungsanträge in dieser Kategorie</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {requests.map((request) => (
                      <div
                        key={request.id}
                        className="p-4 border rounded-lg hover:bg-secondary/20 transition-colors"
                        data-testid={`request-${request.id}`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                          <div className="flex-1">
                            {/* Header row */}
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <StatusBadge status={request.status} />
                              <Badge variant="outline">{CATEGORY_LABELS[request.category] || request.category}</Badge>
                              <span className="text-sm text-muted-foreground">
                                {formatDateTime(request.created_at)}
                              </span>
                            </div>
                            
                            {/* Employee info */}
                            <div className="flex items-center gap-2 mb-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{request.employee_name}</span>
                            </div>
                            
                            {/* Field change */}
                            <p className="font-medium text-lg">{request.field_label}</p>
                            <div className="text-sm text-muted-foreground mt-1 flex items-center flex-wrap gap-1">
                              <span className="line-through bg-red-50 px-2 py-0.5 rounded">
                                {formatIfDate(request.current_value) || '(leer)'}
                              </span>
                              <ChevronRight className="h-4 w-4" />
                              <span className="font-medium text-foreground bg-green-50 px-2 py-0.5 rounded">
                                {formatIfDate(request.new_value)}
                              </span>
                            </div>
                            
                            {/* Reason */}
                            {request.reason && (
                              <p className="text-sm text-muted-foreground mt-2 italic">
                                Begründung: "{request.reason}"
                              </p>
                            )}
                            
                            {/* Rejection reason */}
                            {request.rejection_reason && (
                              <p className="text-sm text-red-500 mt-2">
                                Ablehnungsgrund: {request.rejection_reason}
                              </p>
                            )}
                            
                            {/* Processed info */}
                            {request.processed_by_name && (
                              <p className="text-sm text-muted-foreground mt-2">
                                Bearbeitet von: {request.processed_by_name} am {formatDateTime(request.processed_at)}
                              </p>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openHistoryDialog(request)}
                              data-testid={`history-${request.id}`}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            
                            {request.status === 'pending' && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openActionDialog(request, 'in_progress')}
                                  data-testid={`in-progress-${request.id}`}
                                >
                                  <PlayCircle className="h-4 w-4 mr-1" />
                                  In Bearbeitung
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-500 border-red-200 hover:bg-red-50"
                                  onClick={() => openActionDialog(request, 'rejected')}
                                  data-testid={`reject-${request.id}`}
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Ablehnen
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => openActionDialog(request, 'approved')}
                                  data-testid={`approve-${request.id}`}
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Genehmigen
                                </Button>
                              </>
                            )}
                            
                            {request.status === 'in_progress' && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-500 border-red-200 hover:bg-red-50"
                                  onClick={() => openActionDialog(request, 'rejected')}
                                  data-testid={`reject-${request.id}`}
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Ablehnen
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => openActionDialog(request, 'approved')}
                                  data-testid={`approve-${request.id}`}
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Genehmigen
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Dialog */}
        <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {actionType === 'approved' && 'Antrag genehmigen'}
                {actionType === 'rejected' && 'Antrag ablehnen'}
                {actionType === 'in_progress' && 'In Bearbeitung setzen'}
              </DialogTitle>
              <DialogDescription>
                {actionType === 'approved' && 'Der neue Wert wird automatisch in den Mitarbeiterdaten gespeichert.'}
                {actionType === 'rejected' && 'Bitte geben Sie einen Grund für die Ablehnung an.'}
                {actionType === 'in_progress' && 'Der Antrag wird als "In Bearbeitung" markiert.'}
              </DialogDescription>
            </DialogHeader>
            
            {selectedRequest && (
              <div className="space-y-4">
                <div className="p-3 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Mitarbeiter</p>
                  <p className="font-medium">{selectedRequest.employee_name}</p>
                </div>
                
                <div className="p-3 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">{selectedRequest.field_label}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="line-through">{formatIfDate(selectedRequest.current_value) || '(leer)'}</span>
                    <ChevronRight className="h-4 w-4" />
                    <span className="font-medium">{formatIfDate(selectedRequest.new_value)}</span>
                  </div>
                </div>
                
                {actionType === 'rejected' && (
                  <div className="space-y-2">
                    <Label>Ablehnungsgrund *</Label>
                    <Textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Grund für die Ablehnung eingeben..."
                      rows={3}
                      data-testid="rejection-reason"
                    />
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={handleAction}
                disabled={processing || (actionType === 'rejected' && !rejectionReason.trim())}
                className={
                  actionType === 'approved' ? 'bg-green-600 hover:bg-green-700' :
                  actionType === 'rejected' ? 'bg-red-600 hover:bg-red-700' :
                  ''
                }
                data-testid="confirm-action"
              >
                {processing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : actionType === 'approved' ? (
                  <Check className="h-4 w-4 mr-2" />
                ) : actionType === 'rejected' ? (
                  <X className="h-4 w-4 mr-2" />
                ) : (
                  <PlayCircle className="h-4 w-4 mr-2" />
                )}
                {actionType === 'approved' && 'Genehmigen'}
                {actionType === 'rejected' && 'Ablehnen'}
                {actionType === 'in_progress' && 'In Bearbeitung setzen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Antragsverlauf</DialogTitle>
            </DialogHeader>
            {historyRequest && (
              <div className="space-y-4">
                <div className="p-3 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Mitarbeiter</p>
                  <p className="font-medium">{historyRequest.employee_name}</p>
                </div>
                
                <div className="p-3 bg-secondary/30 rounded-lg">
                  <p className="font-medium">{historyRequest.field_label}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatIfDate(historyRequest.current_value) || '(leer)'} → {formatIfDate(historyRequest.new_value)}
                  </p>
                </div>
                
                <div className="space-y-3">
                  <p className="font-medium">Status-Historie</p>
                  {historyRequest.status_history?.map((entry, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg">
                      <StatusBadge status={entry.status} />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{entry.changed_by_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(entry.timestamp)}
                        </p>
                        {entry.rejection_reason && (
                          <p className="text-sm text-red-500 mt-1">
                            Grund: {entry.rejection_reason}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>
                Schließen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
